import { Injectable } from '@angular/core';
import { VideoPost } from '../models/e621.models';

/**
 * Service responsible for preloading video and image elements in the background.
 * Extracted from VideoPlayerComponent to reduce component complexity.
 *
 * Manages a sequential preload queue, caching preloaded elements, and cleaning up
 * old elements that are too far from the current position.
 */
@Injectable({
  providedIn: 'root',
})
export class MediaPreloadService {
  private preloadedVideos = new Map<number, HTMLVideoElement>();
  private preloadedImages = new Map<number, HTMLImageElement>();
  private readonly MAX_PRELOADED_VIDEOS = 2;
  private isPreloading = false;
  private preloadQueue: VideoPost[] = [];
  private currentPreloadElement: HTMLVideoElement | null = null;
  private getVideos: (() => VideoPost[]) | null = null;
  private getCurrentIndex: (() => number) | null = null;

  /**
   * Start preloading next videos/images sequentially.
   * @param currentVideo The currently displayed video
   * @param getVideos Function that returns all available videos
   * @param getCurrentIndex Function that returns the current index
   */
  startPreloading(
    currentVideo: VideoPost | null,
    getVideos: () => VideoPost[],
    getCurrentIndex: () => number,
  ): void {
    if (this.isPreloading) {
      console.log('Preloading already in progress, skipping...');
      return;
    }

    if (!currentVideo) {
      console.log('No current video, cannot start preloading');
      return;
    }

    console.log(`Starting sequential video preloading for current video: ${currentVideo.id}`);

    // Store callbacks for later use (e.g., position-aware cleanup)
    this.getVideos = getVideos;
    this.getCurrentIndex = getCurrentIndex;

    // Get the next videos to preload
    const videosToPreload = this.getNextVideosToPreload(getVideos, getCurrentIndex);
    console.log(`Found ${videosToPreload.length} videos to preload:`, videosToPreload.map(v => v.id));

    if (videosToPreload.length === 0) {
      console.log('No videos to preload - queue is empty');
      return;
    }

    // Set up the preload queue
    this.preloadQueue = [...videosToPreload];
    this.isPreloading = true;
    console.log(`Preload queue initialized with ${this.preloadQueue.length} videos`);

    // Start preloading the first video in the queue
    this.preloadNextVideoInQueue();
  }

  /**
   * Stop any ongoing preloading
   */
  stopPreloading(): void {
    if (!this.isPreloading) {
      console.log('No preloading to stop');
      return;
    }

    console.log(`Stopping ongoing preloading... (queue had ${this.preloadQueue.length} videos remaining)`);

    // Clear the preload queue
    this.preloadQueue = [];

    // Stop current preloading element if any
    if (this.currentPreloadElement) {
      console.log('Removing current preloading element');
      if (this.currentPreloadElement.parentNode) {
        this.currentPreloadElement.parentNode.removeChild(this.currentPreloadElement);
      }
      this.currentPreloadElement = null;
    }

    // Reset preloading state
    this.isPreloading = false;
    console.log('Preloading stopped');
  }

  /**
   * Check if a video is already preloaded and use it if available
   */
  usePreloadedVideoIfAvailable(videoId: number): boolean {
    const preloadedElement = this.preloadedVideos.get(videoId);
    if (!preloadedElement) return false;

    console.log(`Using preloaded video ${videoId}`);

    // The main video will load normally, but the preloaded video gives us a head start
    // We can't directly replace the element due to Angular's binding, but the browser
    // may use cached data from the preloaded video

    return true;
  }

  /**
   * Check if an image is already preloaded and use it if available
   */
  usePreloadedImageIfAvailable(videoId: number): boolean {
    const preloadedElement = this.preloadedImages.get(videoId);
    if (!preloadedElement) return false;

    console.log(`Using preloaded image ${videoId}`);

    // The main image will load normally, but the preloaded image gives us a head start
    // We can't directly replace the element due to Angular's binding, but the browser
    // may use cached data from the preloaded image

    return true;
  }

  /**
   * Clean up all preloaded elements. Call during component destroy.
   */
  cleanupAll(): void {
    for (const [, element] of this.preloadedVideos.entries()) {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    }
    this.preloadedVideos.clear();

    for (const [, element] of this.preloadedImages.entries()) {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    }
    this.preloadedImages.clear();

    this.preloadQueue = [];
    this.currentPreloadElement = null;
    this.isPreloading = false;
  }

  /**
   * Preload the next video in the queue (sequential processing)
   */
  private preloadNextVideoInQueue(): void {
    console.log(`Processing preload queue: ${this.preloadQueue.length} videos remaining`);

    if (this.preloadQueue.length === 0) {
      console.log('Preload queue empty, stopping preloading');
      this.isPreloading = false;
      return;
    }

    const nextVideo = this.preloadQueue.shift()!;
    console.log(`Preloading next video in queue: ${nextVideo.id} (${this.preloadQueue.length} remaining after this)`);
    this.preloadVideoSequential(nextVideo);
  }

  /**
   * Get the next videos that should be preloaded
   */
  private getNextVideosToPreload(
    getVideos: () => VideoPost[],
    getCurrentIndex: () => number,
  ): VideoPost[] {
    const videos = getVideos();
    const currentIndex = getCurrentIndex();
    const videosToPreload: VideoPost[] = [];

    console.log(`Getting videos to preload: current index=${currentIndex}, total videos=${videos.length}, max preload=${this.MAX_PRELOADED_VIDEOS}`);
    console.log(`Already preloaded videos:`, Array.from(this.preloadedVideos.keys()));

    // Preload next 2-3 videos (wrapping around if needed)
    for (let i = 1; i <= this.MAX_PRELOADED_VIDEOS; i++) {
      const nextIndex = (currentIndex + i) % videos.length;
      const nextVideo = videos[nextIndex];

      if (nextVideo) {
        const alreadyPreloaded = this.preloadedVideos.has(nextVideo.id);
        console.log(`Checking video at index ${nextIndex} (id: ${nextVideo.id}): already preloaded = ${alreadyPreloaded}`);

        if (!alreadyPreloaded) {
          videosToPreload.push(nextVideo);
        }
      } else {
        console.log(`No video found at index ${nextIndex}`);
      }
    }

    console.log(`Videos selected for preloading:`, videosToPreload.map(v => v.id));
    return videosToPreload;
  }

  /**
   * Preload a single video in the background (sequential version)
   */
  private preloadVideoSequential(video: VideoPost): void {
    if (this.preloadedVideos.has(video.id)) {
      console.log(`Video ${video.id} already preloaded, skipping to next`);
      // Continue with next video in queue
      this.preloadNextVideoInQueue();
      return;
    }

    console.log(`Preloading video ${video.id}...`);

    // Create a new video element for preloading
    const preloadElement = document.createElement('video');
    preloadElement.preload = 'auto'; // Use 'auto' to preload the video data
    preloadElement.muted = true; // Ensure it doesn't interfere with current video
    preloadElement.playsInline = true;
    preloadElement.style.display = 'none'; // Hide the preload element

    // Store reference to current preloading element
    this.currentPreloadElement = preloadElement;

    // Set up event handlers
    preloadElement.addEventListener('loadeddata', () => {
      console.log(`Video ${video.id} preloaded successfully`);
      this.preloadedVideos.set(video.id, preloadElement);
      this.cleanupOldPreloadedVideos();

      // Clear current preload reference
      this.currentPreloadElement = null;

      // Continue with next video in queue
      this.preloadNextVideoInQueue();
    });

    preloadElement.addEventListener('error', (error) => {
      console.warn(`Failed to preload video ${video.id}:`, error);
      // Clean up the failed preload element
      if (preloadElement.parentNode) {
        preloadElement.parentNode.removeChild(preloadElement);
      }

      // Clear current preload reference
      this.currentPreloadElement = null;

      // Continue with next video in queue despite the error
      this.preloadNextVideoInQueue();
    });

    // Add timeout to prevent hanging on slow/failed loads
    setTimeout(() => {
      if (this.currentPreloadElement === preloadElement) {
        console.warn(`Preload timeout for video ${video.id}, moving to next`);
        if (preloadElement.parentNode) {
          preloadElement.parentNode.removeChild(preloadElement);
        }
        this.currentPreloadElement = null;
        this.preloadNextVideoInQueue();
      }
    }, 10000); // 10 second timeout

    // Add to DOM (hidden) and start loading
    document.body.appendChild(preloadElement);
    preloadElement.src = video.url;
  }

  /**
   * Clean up old preloaded videos to manage memory.
   * Uses position-aware cleanup when video list context is available,
   * otherwise falls back to FIFO eviction.
   */
  private cleanupOldPreloadedVideos(): void {
    if (this.preloadedVideos.size <= this.MAX_PRELOADED_VIDEOS) return;

    if (this.getVideos && this.getCurrentIndex) {
      // Position-aware cleanup: remove videos that are too far from current position
      const currentIndex = this.getCurrentIndex();
      const videos = this.getVideos();
      for (const [videoId, element] of this.preloadedVideos.entries()) {
        const videoIndex = videos.findIndex((v: VideoPost) => v.id === videoId);
        const distance = Math.abs(videoIndex - currentIndex);

        if (distance > this.MAX_PRELOADED_VIDEOS) {
          console.log(`Cleaning up preloaded video ${videoId} (distance: ${distance})`);
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
          this.preloadedVideos.delete(videoId);
        }
      }
    } else {
      // Fallback: FIFO eviction when position info is not available
      const entries = Array.from(this.preloadedVideos.entries());
      while (entries.length > this.MAX_PRELOADED_VIDEOS) {
        const [videoId, element] = entries.shift()!;
        console.log(`Cleaning up preloaded video ${videoId}`);
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
        this.preloadedVideos.delete(videoId);
      }
    }
  }
}
